<template>
  <v-flex
    xs12
    align-self-center
    justify-center
  >
    <div>
      <v-container fill-height fluid>
        <v-row>
          <v-flex xs2 class="ml-2">
            <InputDataPicker
              v-model="filters.date_from"
              :label="$t('fields.date_from')"
              :clearable="true"
            />
          </v-flex>

          <v-flex xs2 class="ml-2">
            <InputDataPicker
              v-model="filters.date_to"
              :label="$t('fields.date_to')"
              :clearable="true"
            />
          </v-flex>

          <v-flex xs2 class="mt-6 ml-2">
            <PropagandistSelect
              v-model="filters.propagandist_id"
              :label="$t('fields.propagandist')"
            />
          </v-flex>

          <v-flex xs1 class="mt-6 ml-2">
            <v-text-field
              v-model="filters.estimate_id"
              :label="$t('ESTIMATE_NUMBER')"
              clearable
              type="number"
            />
          </v-flex>

          <v-spacer></v-spacer>

          <v-btn
            class="primary mr-2"
            :loading="creating"
            @click="createEstimate()"
          >{{ $t('ESTIMATE_NEW') }}
          </v-btn>
        </v-row>
      </v-container>

      <v-row>
        <v-col cols="12">
          <v-card flat light class="pa-2">
            <v-data-table
              :headers="headers"
              :items="items"
              item-key="id"
              :server-items-length="totalItems"
              :no-data-text="$t('NO_DATA')"
              :no-results-text="$t('NO_DATA')"
              hide-default-footer
              :options.sync="pagination"
              :loading="loading"
              class="elevation-1"
            >
              <template v-slot:[`header.date_from`]="{ header }">
                <HeaderHint :text="header.text" :hint="$t('ESTIMATE_HINT_DATE_FROM')" />
              </template>
              <template v-slot:[`header.date_to`]="{ header }">
                <HeaderHint :text="header.text" :hint="$t('ESTIMATE_HINT_DATE_TO')" />
              </template>
              <template v-slot:[`header.qty_needed`]="{ header }">
                <HeaderHint :text="header.text" :hint="$t('ESTIMATE_HINT_MUST_GO_OUT')" />
              </template>
              <template v-slot:[`header.qty_backlog`]="{ header }">
                <HeaderHint :text="header.text" :hint="$t('ESTIMATE_HINT_BACKLOG')" />
              </template>
              <template v-slot:[`header.qty_missing`]="{ header }">
                <HeaderHint :text="header.text" :hint="$t('ESTIMATE_HINT_MISSING')" />
              </template>

              <template v-slot:[`item.date_from`]="{ item }">
                {{ item.date_from | moment('DD/MM/YYYY HH:mm') }}
              </template>

              <template v-slot:[`item.date_to`]="{ item }">
                {{ item.date_to | moment('DD/MM/YYYY HH:mm') }}
              </template>

              <template v-slot:[`item.chained`]="{ item }">
                <v-chip v-if="!item.chained" small color="grey lighten-2">
                  {{ $t('ESTIMATE_RECALC_LABEL') }}
                </v-chip>
              </template>

              <template v-slot:[`item.qty_needed`]="{ item }">
                {{ total(item, 'qty_needed') }}
              </template>

              <template v-slot:[`item.qty_missing`]="{ item }">
                <span :class="total(item, 'qty_missing') > 0 ? 'red--text font-weight-bold' : ''">
                  {{ total(item, 'qty_missing') }}
                </span>
              </template>

              <template v-slot:[`item.qty_backlog`]="{ item }">
                {{ total(item, 'qty_backlog') }}
              </template>

              <template v-slot:[`item.actions`]="{ item }">
                <v-btn
                  icon
                  :title="$t('ESTIMATE_OPEN')"
                  @click="openDetail(item)"
                >
                  <v-icon>visibility</v-icon>
                </v-btn>
                <v-btn
                  icon
                  :title="$t('ESTIMATE_DOWNLOAD_ORDER')"
                  :loading="downloadingId === item.id"
                  @click="downloadOrderFile(item)"
                >
                  <v-icon>cloud_download</v-icon>
                </v-btn>
              </template>
            </v-data-table>
          </v-card>

          <Paginator
            :total-items="totalItems"
            :page.sync="pagination.page"
            :items-per-page.sync="pagination.itemsPerPage"
            @change="fetchEstimates"
          />
        </v-col>
      </v-row>
    </div>
  </v-flex>
</template>

<script>
  import { mapActions, mapGetters } from 'vuex';

  import { estimatesService } from '../../../utils/services/estimates.service';
  import InputDataPicker from '../../../components/shared/Inputs/InputDataPicker.vue';
  import PropagandistSelect from '../../../components/shared/Vselects/PropagandistSelect.vue';
  import EstimateModal from '../../../components/modals/Estimates/EstimateModal.vue';
  import HeaderHint from '../../../components/shared/HeaderHint.vue';
  import Paginator from '../../../components/shared/Paginator';
  import { routeNames } from '../../../config/router/routeNames';

  export default {
    name: 'CampaignReportEstimates',
    components: {
      InputDataPicker,
      PropagandistSelect,
      HeaderHint,
      Paginator,
    },
    data() {
      return {
        loading: false,
        creating: false,
        downloadingId: null,
        items: [],
        totalItems: 0,
        pagination: {
          page: 1,
          itemsPerPage: 10,
          sortDesc: [true],
          // Deve essere il `value` dell'header, non il nome della colonna:
          // e' quello che Vuetify confronta per accendere la freccetta.
          sortBy: ['id'],
        },
        headers: [
          { text: this.$t('ESTIMATE_NUMBER'), value: 'id' },
          { text: this.$t('fields.date_from'), value: 'date_from' },
          { text: this.$t('fields.date_to'), value: 'date_to' },
          { text: '', value: 'chained', sortable: false },
          { text: this.$t('fields.propagandist'), value: 'propagandist', sortable: false },
          { text: this.$t('ESTIMATE_MUST_GO_OUT'), value: 'qty_needed', sortable: false },
          { text: this.$t('ESTIMATE_BACKLOG'), value: 'qty_backlog', sortable: false },
          { text: this.$t('ESTIMATE_MISSING'), value: 'qty_missing', sortable: false },
          { text: this.$t('fields.actions'), value: 'actions', sortable: false, align: 'center' },
        ],
        filters: {
          date_from: null,
          date_to: null,
          propagandist_id: 0,
          estimate_id: null,
        },
      };
    },
    computed: {
      ...mapGetters('user', ['currentUser']),
      requestFilters() {
        const filters = {
          date_from: this.filters.date_from,
          date_to: this.filters.date_to,
          propagandist_id: this.filters.propagandist_id,
          estimate_id: this.filters.estimate_id,
        };

        if (this.pagination.sortBy.length === 1 && this.pagination.sortDesc.length === 1) {
          const { sortBy, sortDesc } = this.pagination;
          filters.sortBy = sortBy;
          filters.sortDesc = sortDesc;
        }
        return filters;
      },
    },
    methods: {
      ...mapActions('confirm', ['showConfirm']),
      ...mapActions('modal', ['showModal', 'hideModal']),
      ...mapActions('alert', ['showToast']),

      total(item, key) {
        return item.totals && item.totals[key] !== undefined ? item.totals[key] : 0;
      },

      openDetail(item) {
        this.$router.push({
          name: routeNames.CAMPAIGN_REPORT_ESTIMATE_DETAIL,
          params: { id: item.id },
        });
      },

      downloadOrderFile(item) {
        this.downloadingId = item.id;
        return estimatesService.exportOrderFile(item.id)
          .then(response => {
            if (!response.data) {
              this.showToast({ type: 'info', message: this.$t('ESTIMATE_NOTHING_MISSING') });
              return;
            }
            const link = document.createElement('a');
            const file = new Blob([response.data], { type: 'text/plain' });
            link.href = URL.createObjectURL(file);
            link.download = `stima ${item.id}.txt`;
            link.click();
            URL.revokeObjectURL(link.href);
          })
          .catch(error => {
            this.handleError(error, this.errors);
          })
          .finally(() => {
            this.downloadingId = null;
          });
      },

      createEstimate() {
        if (this.currentUser.user.is_propagandist) {
          this.confirmOwnEstimate();
        } else {
          this.choosePropagandist();
        }
      },

      confirmOwnEstimate() {
        this.showConfirm({
          message: this.$t('ESTIMATE_NEW_HINT'),
          onConfirm: () => this.generate({ propagandist_id: this.currentUser.user.id }),
        });
      },

      choosePropagandist() {
        this.showModal({
          component: EstimateModal,
          data: { stored: {} },
          options: {
            title: this.$t('ESTIMATE_NEW'),
            onSave: form => this.generate(form).then(() => this.hideModal({})),
          },
        });
      },

      generate(form) {
        this.creating = true;
        return estimatesService.create(form)
          .then(response => {
            this.showToast({ type: 'success', message: this.$t('ESTIMATE_CREATED') });
            const estimate = response.data && response.data.estimate;
            if (estimate) {
              this.openDetail(estimate);
            }
          })
          .catch(error => {
            this.handleError(error, this.errors);
          })
          .finally(() => {
            this.creating = false;
            this.fetchEstimates(1);
          });
      },

      async fetchEstimates(forcePage) {
        this.loading = true;
        const { page, itemsPerPage } = this.pagination;
        return estimatesService.index({
          page: forcePage || page || 1,
          itemsPerPage,
          filters: this.requestFilters,
        })
          .then(response => {
            this.items = response.data.data;
            this.totalItems = response.data.meta.total;
            this.pagination = this.getPagination(
              response.data.meta,
              { oldPagination: this.pagination, forcePage },
            );
          })
          .catch(error => {
            this.handleError(error, this.errors);
          })
          .finally(() => {
            this.loading = false;
          });
      },
    },
    async created() {
      await this.fetchEstimates(1);
    },
    watch: {
      filters: {
        deep: true,
        handler() {
          this.fetchEstimates(1);
        },
      },
      'pagination.sortBy': {
        handler(oldVal, newVal) {
          if (newVal !== oldVal) {
            this.fetchEstimates(1);
          }
        },
      },
    },
  };
</script>
